import { useProcessing as usePanoramaProcessing } from "./usePanoramaProcessing"
import {
    selectSet,
    selectUploadsForSetSummary,
    selectSelectedUpload,
    setSelectedUpload,
    deleteUpload,
    uploadFlight,
    stageFiles,
    selectUploadsForSet,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
} from "./PanoramaSlice"
import { PanoramaPreviewFiles } from "./PreviewFiles"
import {
    deletePanoramaFlight,
    updatePanoramaFlight,
} from "features/pubsub/entityActions/flights/panorama"
import {
    GenericJob,
    ImageProcessingState,
    isUsable,
    JobStatusType,
    SampleDataFlightKey,
    UploadComponentProps,
} from "@nova/constants"
import { selectPanoramaFlightById } from "features/entity-caching/flights/selectors"
import {
    selectIsPanoramaFlightUploading,
    selectPanoramaFinishedUploadSize,
    selectPanoramaImageAndUploadByFlightId,
    selectPanoramaUploadSize,
    selectSummaryFromPanoramaUpload,
} from "features/entity-caching/images/panorama/selectors"
import {
    CreateUploadFlow,
    CreateUploadFlowProps,
    getGenericUploadFlowRenderComponent,
} from "panels/content/side-panel/upload-flow/UploadFlow"
import { activateSidePanelModal } from "panels/content/side-panel/thunks"
import { GenericAddFiles } from "panels/content/side-panel/upload-flow/GenericAddFiles"
import { memoize } from "nodash"
import { createSelector } from "@reduxjs/toolkit"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useEffect } from "react"
import { setActiveFeatureId } from "panels/content/side-panel/slice.new"

const selectDummyJob = memoize((flightId: string) =>
    createSelector(
        [selectPanoramaFlightById(flightId), selectUploadsForSet(flightId)],
        (flight, uploads) =>
            (flight &&
            uploads?.every(
                (upload) => isUsable(upload)?.state === ImageProcessingState.PendingUpload
            )
                ? {
                      id: flightId,
                      status: JobStatusType.QUEUED,
                      createdAt: Math.round(Date.now() / 1000),
                  }
                : undefined) as GenericJob
    )
)

const PanoramaUploadFlow = CreateUploadFlow({
    selectSet,
    selectJob: selectDummyJob,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
    selectUploadsForSet: selectPanoramaImageAndUploadByFlightId,
    selectUploadsForSetSummary,
    selectSelectedUpload,
    setSelectedUpload,
    deleteUpload,
    stageFiles,
    uploadFlight,
    deleteFlight: deletePanoramaFlight,
    useProcessing: usePanoramaProcessing,
    supportedFileUploadExtensions: ["jpg", "jpeg"],
    minUploads: 1,
    maxUploads: 1,
    skipProcessing: true,
    selectFinishedUploadSize: selectPanoramaFinishedUploadSize,
    selectFlightById: selectPanoramaFlightById,
    selectIsUploading: selectIsPanoramaFlightUploading,
    selectUploadSize: selectPanoramaUploadSize,
    selectUploadStateSummary: selectSummaryFromPanoramaUpload,
    updateFlight: updatePanoramaFlight,
    setSelectedPlugin: activateSidePanelModal,
    pluginImageUrl: "/images/plugins/layers/panorama.svg",
    pluginId: NovaPluginIds.Panorama,
    sampleDataFlightKey: SampleDataFlightKey.pano1,
} as CreateUploadFlowProps)

const PanoramaRenderComponent = getGenericUploadFlowRenderComponent({
    AddFiles: GenericAddFiles,
    PreviewFiles: PanoramaPreviewFiles,
})

export const PanoramaMultifileUpload = (props: UploadComponentProps) => {
    const dispatch = useAppDispatch()
    const { id } = props
    const pano = useAppSelector(selectPanoramaFlightById(id))
    useEffect(() => {
        if (pano?.demoId) {
            dispatch(setActiveFeatureId({ feature: undefined }))
        }
    }, [dispatch, pano?.demoId])
    return (
        <PanoramaUploadFlow {...props}>
            <PanoramaRenderComponent />
        </PanoramaUploadFlow>
    )
}
